import { AbstractControl, AsyncValidatorFn, FormBuilder, Validators } from '@angular/forms';
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { GridModule } from '@syncfusion/ej2-angular-grids';
import { ClientDTO, FilterDTO, FilterOperator, ParkerCompanyDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ClientService } from 'src/app/base/services/client/client.service';
import { CompanyAdministrationService } from './company-administration.service';
import { DataManager } from '@syncfusion/ej2-data';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CompanyFormComponent } from './company-form/company-form.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';

import { ClientAutocompleteComponent } from '../../../base/shared/client-autocomplete/client-autocomplete.component';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';

@Component({
    selector: 'app-company-administration',
    templateUrl: './company-administration.component.html',
    styleUrls: ['./company-administration.component.scss'],
    imports: [TitleBarComponent, ClientAutocompleteComponent, SearchFieldComponent, CheckBoxModule, GridModule, ToolbarModule, CompanyFormComponent, TranslateModule]
})
export class CompanyAdministrationComponent extends GridBaseComponent implements OnInit {

  constructor(
    clientService: ClientService,
    translateService: TranslateService,
    private companyAdministrationService: CompanyAdministrationService,
    private fb: FormBuilder,
    private spinnerService: SpinnerService
  ) {
    super(clientService, translateService, companyAdministrationService);
    this.searchOptions = {fields: ['name']};
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    return !this.gridEditForm?.dirty;
  }

  ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.companyAdministrationService, this.spinnerService),
    });
    this.isActiveColumnName = 'active';
    this.editDialogTitle = 'i18n.domain.company-administration.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.company-administration.add-dialog-title';
    this.addIndexFindColumnName = 'name';
  }

  public override get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public override set selectedClient(value: ClientDTO) {
    this.clientService.selectedClient = value;
    this.onClientSelected(value);
  }

  protected initializeEditForm(data: ParkerCompanyDTO): void {
    this.gridEditForm = this.fb.group({
      id: this.fb.control(data.id),
      name: this.fb.control(
        data.name,
        [Validators.required],
        [this.companyNameValidator(data.id)]
      ),
      active: this.fb.control(data.active),
    });
  }

  protected getEditDialogTitleParams(rowData): any {
    return {params: rowData.name};
  }

  private companyNameValidator(currentCompanyId: number): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const nameFilter = new FilterDTO({
        isComplex: false,
        field: 'Name',
        operator: FilterOperator.Equal,
        value: control.value
      });
      return timer(500).pipe(
        switchMap(() =>
          this.companyAdministrationService.getData(0, 0, [nameFilter], 'id', SortDirection.Ascending)
        ),
        map(res => {
          const isNameInUse =
            res.totalNumberOfItems > 0 &&
            res.items.some(company => company.id !== currentCompanyId);
          const sameAsOriginal =
            res.items.find(company => company.id === currentCompanyId)?.name === control.value;
          return sameAsOriginal || !isNameInUse ? null : { nameAlreadyInUse: true };
        })
      );
    };
  }
}
